/**
 * ============================================================================
 *                              crmss 版权声明
 * ============================================================================
 * 本软件系统（crmss - 快速开发通用管理后台框架）所有代码版权归 crmss 团队所有。
 * 代码中的 crmss 版权声明不允许删除和修改。
 * ============================================================================
 */

import CryptoJS from 'crypto-js'

/**
 * API签名工具类
 * 用于生成API请求签名
 */

// 应用ID（生产环境应从配置读取）
const APP_ID = 'crmss-web'

// 签名密钥（生产环境应从环境变量读取）
const SECRET_KEY = 'crmss-api-secret-key-2024-secure-signature'

/**
 * 生成随机nonce
 */
export function generateNonce(): string {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15)
}

/**
 * 生成时间戳
 */
export function generateTimestamp(): string {
  return Date.now().toString()
}

/**
 * 生成签名
 * @param params 请求参数
 * @param timestamp 时间戳
 * @param nonce 随机数
 * @returns 签名
 */
export function generateSignature(
  params: Record<string, any>,
  timestamp: string,
  nonce: string
): string {
  // 1. 构建参数字符串（按字母顺序排序）- 与后端算法保持一致
  const sortedParams: string[] = []

  // 添加业务参数
  const keys = Object.keys(params).sort()
  for (const key of keys) {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      sortedParams.push(`${key}=${params[key]}`)
    }
  }

  // 2. 添加系统参数（注意顺序：timestamp在前，nonce在后）
  sortedParams.push(`timestamp=${timestamp}`)
  sortedParams.push(`nonce=${nonce}`)

  // 3. 构建签名字符串
  const signString = sortedParams.join('&') + `&key=${SECRET_KEY}`

  // 4. MD5加密并转大写
  const signature = CryptoJS.MD5(signString).toString().toUpperCase()

  console.log('[SIGN_DEBUG_FRONTEND] ========== 签名调试信息 ==========')
  console.log('[SIGN_DEBUG_FRONTEND] 参数:', params)
  console.log('[SIGN_DEBUG_FRONTEND] 时间戳:', timestamp)
  console.log('[SIGN_DEBUG_FRONTEND] Nonce:', nonce)
  console.log('[SIGN_DEBUG_FRONTEND] 签名字符串:', signString)
  console.log('[SIGN_DEBUG_FRONTEND] 生成签名:', signature)
  console.log('[SIGN_DEBUG_FRONTEND] ====================================')

  return signature
}

/**
 * 获取签名请求头
 * @param params 请求参数（可以是对象或JSON字符串）
 * @returns 签名请求头
 */
export function getSignatureHeaders(params: Record<string, any> = {}, rawBody?: string): Record<string, string> {
  const timestamp = generateTimestamp()
  const nonce = generateNonce()

  // 将参数转换为签名用的格式
  // 如果有body数据，将其整体作为 _body 参数（与后端保持一致）
  const signParams: Record<string, any> = {}
  if (rawBody && rawBody.length > 0) {
    // 使用原始body字符串（确保与请求发送的body完全一致）
    signParams['_body'] = rawBody
  } else if (params && Object.keys(params).length > 0) {
    // 对对象键进行排序，确保JSON字符串格式稳定
    const sortedParams: Record<string, any> = {}
    Object.keys(params).sort().forEach(key => {
      sortedParams[key] = params[key]
    })
    signParams['_body'] = JSON.stringify(sortedParams)
  }

  const signature = generateSignature(signParams, timestamp, nonce)

  return {
    'X-Timestamp': timestamp,
    'X-Nonce': nonce,
    'X-Signature': signature
  }
}

/**
 * 需要签名的请求方法
 */
const SIGNATURE_METHODS = ['POST', 'PUT', 'DELETE', 'PATCH']

/**
 * 不需要签名的URL路径
 */
const EXCLUDE_URLS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh-token',
  '/uploads/'
]

/**
 * 检查是否需要签名
 * @param url 请求URL
 * @param method 请求方法
 * @returns 是否需要签名
 */
export function needSignature(url: string, method: string): boolean {
  // 检查请求方法
  if (!SIGNATURE_METHODS.includes(method.toUpperCase())) {
    return false
  }
  
  // 检查排除路径
  for (const excludeUrl of EXCLUDE_URLS) {
    if (url.includes(excludeUrl)) {
      return false
    }
  }
  
  return true
}
